import React, { useEffect, useRef } from "react";
import { importAmCharts5Core, importAmCharts5XY, importAmCharts5Theme } from "../../libs/amcharts5/imports";

const HorizontalStackedBarChart = ({ data, seriesColors, id }) => {

    const chartRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const chart_div = `chart_${id}`;

    useEffect(() => {
        if (typeof window === "undefined" || !data || !containerRef.current) return;

        const initChart = async () => {
            try {
                // Lazy load AmCharts 5 modules
                await importAmCharts5Core();
                await importAmCharts5XY();
                await importAmCharts5Theme("Animated");

                const am5 = (window as any).am5;
                const am5xy = (window as any).am5xy;
                const AnimatedTheme = (window as any).am5themes_Animated;

                if (!am5 || !am5xy) {
                    console.warn("amCharts5 not loaded");
                    return;
                }

                // Dispose previous chart if exists
                if (chartRef.current) {
                    chartRef.current.dispose();
                }

                // Create root element
                const root = am5.Root.new(containerRef.current);
                if (AnimatedTheme) {
                    root.setThemes([AnimatedTheme.new(root)]);
                }

                // Create chart
                const chart = root.container.children.push(
                    am5xy.XYChart.new(root, {
                        panX: false,
                        panY: false,
                        wheelX: "none",
                        wheelY: "none",
                        layout: root.verticalLayout,
                    })
                );

                // Category Y-axis (horizontal bars)
                const categoryAxis = chart.yAxes.push(
                    am5xy.CategoryAxis.new(root, {
                        categoryField: id,
                        renderer: am5xy.AxisRendererY.new(root, {
                            inversed: true,
                            cellStartLocation: 0.1,
                            cellEndLocation: 0.9,
                        }),
                    })
                );

                categoryAxis.get("renderer").grid.template.setAll({
                    visible: false,
                });

                // Value X-axis
                const valueAxis = chart.xAxes.push(
                    am5xy.ValueAxis.new(root, {
                        renderer: am5xy.AxisRendererX.new(root, {
                            minGridDistance: 10,
                        }),
                    })
                );

                valueAxis.get("renderer").grid.template.setAll({
                    visible: false,
                });

                // Create series and set custom colors
                const seriesNames = Object.keys(seriesColors);
                seriesNames.forEach((fieldName) => {
                    const series = chart.series.push(
                        am5xy.ColumnSeries.new(root, {
                            name: fieldName,
                            xAxis: valueAxis,
                            yAxis: categoryAxis,
                            valueXField: fieldName,
                            categoryYField: id,
                            stacked: true,
                        })
                    );

                    series.columns.template.setAll({
                        tooltipText: `{name}: [bold]{valueX}[/]`,
                        fill: am5.color(seriesColors[fieldName]),
                        stroke: am5.color(seriesColors[fieldName]),
                    });
                });

                // Set data
                chart.series.each((series: any) => {
                    series.data.setAll(data);
                });
                categoryAxis.data.setAll(data);

                // Add legend
                const legend = chart.children.push(
                    am5.Legend.new(root, {
                        centerX: am5.p50,
                        x: am5.p50,
                    })
                );
                legend.data.setAll(chart.series.values);

                // Animate
                chart.series.each((series: any) => {
                    series.appear();
                });
                chart.appear(1000, 100);

                chartRef.current = root;
            } catch (error) {
                console.error("Error initializing AmCharts 5 chart:", error);
            }
        };

        initChart();

        return () => {
            if (chartRef.current) {
                chartRef.current.dispose();
                chartRef.current = null;
            }
        };
    }, [data, seriesColors, id]);

    return <div ref={containerRef} id={chart_div} style={{ width: '100%', height: '600px' }} />;

};

export default HorizontalStackedBarChart;
